import React, { useMemo } from 'react'
import styled from 'styled-components'
import moment from 'moment'
import { ConnectionCancellationPolicies, formatDate } from '@luxuryescapes/lib-refunds'

import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Clickable from 'components/Common/Clickable'
import useToggle from 'hooks/useToggle'
import BodyText from 'LuxKit/Typography/BodyText'
import HelpPane from 'components/Common/HelpPane'
import Modal from 'LuxKit/Modal/Modal'
import LineRefundIcon from 'LuxKit/Icons/line/LineRefundIcon'
import TextLink from 'LuxKit/TextLink'
import { Refundable, getRefundDescriptions } from '../utils/getCancellationPolicyStatus'

const MiddledTooltipIcon = styled(LineInfoCircleIcon)`
  vertical-align: middle;
`

interface Props {
  cancellationPolicies: ConnectionCancellationPolicies;
  cancellationPolicy?: App.OfferCancellationPolicy;
  checkIn?: string;
  className?: string;
  hideNonRefundableMessage?: boolean;
  textLinkFormat?: boolean
  hideExcludesFlights?: boolean;
  offerType?: string;
}

function ConnectionCancellationPoliciesComponent(props: Props) {
  const { cancellationPolicies, cancellationPolicy, className, checkIn, hideNonRefundableMessage, textLinkFormat, hideExcludesFlights, offerType } = props
  const [isPolicyOpen, togglePolicy] = useToggle()

  const deadline = cancellationPolicies.policies[0]?.deadline ?? null
  const timezone = cancellationPolicies.policies[0]?.timezone ?? null

  // The policy is partially refundable until the first non-refundable deadline
  const firstNonRefundableDeadline = cancellationPolicies.policies.find(policy => policy.nonRefundable)?.deadline ?? null
  const partiallyRefundableUntil = firstNonRefundableDeadline ?? checkIn

  const refundDescriptions = useMemo(() => getRefundDescriptions(offerType), [offerType])
  let refundable: Refundable = Refundable.Non

  // If there is at least 1 policy, and we are before every non-refundable policy's deadline (or the check-in date if there are no non-refundable policies), then the booking is partially refundable
  if (deadline && partiallyRefundableUntil && moment.utc().isBefore(partiallyRefundableUntil)) {
    refundable = Refundable.Partial
  }

  // If there is at least 1 policy, and we are before every policy's deadline, then the booking is fully refundable
  if (deadline && moment.utc().isBefore(deadline)) {
    refundable = Refundable.Fully
  }

  const onClickIcon: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault()
    togglePolicy()
  }

  return <>
    <Clickable onClick={onClickIcon} className={className}>
      {!hideNonRefundableMessage && <>
        {refundable === Refundable.Fully && <>
          {textLinkFormat &&
            <TextLink
              size="medium"
              colour="highlight-secondary"
              underline={false}
              startIcon={<LineRefundIcon />}
            >
              {refundDescriptions[refundable]}
            </TextLink>}
          {!textLinkFormat && <>
            <BodyText variant="medium" weight="bold" colour="highlight-secondary" as="span">{refundDescriptions[refundable]}</BodyText>{' '}
            {deadline &&
              <BodyText variant="medium" as="span">until {formatDate.formattedShortDateByTimezone(deadline, timezone)} (Hotel only)</BodyText>}
            {' '}<MiddledTooltipIcon size="XS" />
          </>}
        </>}
        {refundable === Refundable.Partial && <>
          {textLinkFormat &&
            <TextLink
              size="medium"
              colour="highlight-secondary"
              underline={false}
              startIcon={<LineRefundIcon />}
            >
              {refundDescriptions[refundable]}
            </TextLink>}
          {!textLinkFormat && <>
            <BodyText variant="medium" weight="bold" colour="highlight-secondary" as="span">Partially refundable</BodyText>{' '}
            {partiallyRefundableUntil &&
              <BodyText variant="medium" as="span">until {formatDate.formattedShortDateByTimezone(partiallyRefundableUntil, timezone)} (Hotel only)</BodyText>}
            {' '}<MiddledTooltipIcon size="XS" />
          </>}
        </>}
        {refundable === Refundable.Non && <>
          <BodyText variant="medium" as="span">{refundDescriptions[refundable]}</BodyText>
          {' '}<MiddledTooltipIcon size="XS" />
        </>}
      </>}
      {hideNonRefundableMessage && refundable === Refundable.Fully && <>
        {textLinkFormat &&
          <TextLink
            size="medium"
            colour="highlight-secondary"
            underline={false}
            startIcon={<LineRefundIcon />}
          >
            Free cancellation available
          </TextLink>}
        {!textLinkFormat && <>
          <BodyText variant="medium" weight="bold" colour="highlight-secondary" as="span">Free cancellation available</BodyText>{' '}
          {deadline &&
            <BodyText variant="medium" as="span"> until {formatDate.formattedShortDateByTimezone(deadline, timezone)} (Hotel only)</BodyText>}
          {' '}<MiddledTooltipIcon size="XS" />
        </>}
      </>}
    </Clickable>
    <Modal
      title={refundDescriptions[refundable]}
      isOpen={isPolicyOpen}
      onClose={onClickIcon}
    >
      {cancellationPolicies.description?.map(line => <BodyText variant="large" as="p" key={line}>{ line }</BodyText>)}
      {cancellationPolicy?.type == 'dynamic' &&
        <BodyText variant="large" as="p">Please note: cancellation policies apply to accommodation bookings only{hideExcludesFlights ? '' : ', excludes flights'}.</BodyText>}
      <HelpPane />
    </Modal>
  </>
}

export default ConnectionCancellationPoliciesComponent
